import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import useToken from '../../hooks/useToken';

const LOG_ENABLED = true; // Set to false to disable logging

export default function DocumentViewPage() {
  const { website_id } = useParams();
  const { token, refreshAccessToken } = useToken();
  const [ucSvg, setUcSvg] = useState(null);
  const [contextSvg, setContextSvg] = useState(null);
  const [containerSvg, setContainerSvg] = useState(null);
  const [componentSvg, setComponentSvg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const authRef = useRef(null);
  const loadedRef = useRef(false);

  const logEvent = async (type, details = {}) => {
    if (!LOG_ENABLED) return;
    let auth = authRef.current || (await refreshAccessToken().catch(() => null));
    if (!auth) return;

    const payload = { event: type, timestamp: new Date().toISOString(), ...details };
    fetch('/api/design-document/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const auth = token || await refreshAccessToken();
        authRef.current = auth;
        
        const endpoints = [
          `/api/website/${website_id}/design-document-uc`,
          `/api/website/${website_id}/design-document-context`,
          `/api/website/${website_id}/design-document-container`,
          `/api/website/${website_id}/design-document-component`,
        ];

        const results = await Promise.all(endpoints.map(ep => fetch(ep, { headers: { Authorization: `Bearer ${auth}` } }).then(r => r.ok ? r.json() : null).catch(() => null)));

        setUcSvg(results[0]?.svg || null);
        setContextSvg(results[1]?.svg || null);
        setContainerSvg(results[2]?.svg || null);
        setComponentSvg(results[3]?.svg || null);
        
        // Log page open
        logEvent('open', { page: 'DESIGN_DOCUMENT_VIEWER', website_id });
      } catch (err) {
        console.error(err);
        setError('Failed to load diagrams');
      } finally {
        setLoading(false);
      }
    })();
  }, [website_id, token, refreshAccessToken]);

  // Separate effect for visibility tracking
  useEffect(() => {
    const onVis = () => logEvent('visibility_change', { visible: !document.hidden });
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  if (loading) return <Container className="py-5"><Spinner animation="border" /></Container>;
  if (error) return <Container className="py-5"><Alert variant="danger">{error}</Alert></Container>;

  const handleImageInteraction = (diagramType, interactionType) => {
    logEvent(interactionType, { diagram_type: diagramType });
  };

  return (
    <Container className="py-5">
      <h2 className="mb-4">Design Document Viewer</h2>

      <Card className="mb-3 p-3">
        <h5>Use Case Diagram</h5>
        {ucSvg ? (
          <div 
            dangerouslySetInnerHTML={{ __html: ucSvg }} 
            onMouseEnter={() => handleImageInteraction('use_case', 'hover')}
            onClick={() => handleImageInteraction('use_case', 'click')}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div className="text-muted">Not available</div>
        )}
      </Card>

      <Card className="mb-3 p-3">
        <h5>Context Diagram</h5>
        {contextSvg ? (
          <div 
            dangerouslySetInnerHTML={{ __html: contextSvg }} 
            onMouseEnter={() => handleImageInteraction('context', 'hover')}
            onClick={() => handleImageInteraction('context', 'click')}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div className="text-muted">Not available</div>
        )}
      </Card>

      <Card className="mb-3 p-3">
        <h5>Container Diagram</h5>
        {containerSvg ? (
          <div 
            dangerouslySetInnerHTML={{ __html: containerSvg }} 
            onMouseEnter={() => handleImageInteraction('container', 'hover')}
            onClick={() => handleImageInteraction('container', 'click')}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div className="text-muted">Not available</div>
        )}
      </Card>

      <Card className="mb-3 p-3">
        <h5>Component Diagram</h5>
        {componentSvg ? (
          <div 
            dangerouslySetInnerHTML={{ __html: componentSvg }} 
            onMouseEnter={() => handleImageInteraction('component', 'hover')}
            onClick={() => handleImageInteraction('component', 'click')}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div className="text-muted">Not available</div>
        )}
      </Card>
    </Container>
  );
}
